import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface BatchLine {
  id: number;
  amount: string;
  bank_name: string | null;
  bank_account_name: string | null;
  status: string;
  benefit_claim: { id: number; reference: string; member: { id: number; user: { first_name: string; last_name: string } | null } };
}

interface BatchDetail {
  id: number;
  batch_number: string;
  status: string;
  total_amount: string;
  lines: BatchLine[];
}

export function BenefitPaymentBatchDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [batch, setBatch] = useState<BatchDetail | null>(null);

  const canDisburse = can('memberships.benefits.disburse');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const load = () => api.get<BatchDetail>(`/benefit-payment-batches/${id}`).then((res) => setBatch(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const exportBatch = () => {
    if (!batch) return;
    downloadFile(api, `/benefit-payment-batches/${batch.id}/export`, `${batch.batch_number}.csv`).then(load);
  };

  const markDisbursed = async () => {
    if (!batch) return;
    if (!(await confirmAction('Mark this batch as disbursed once the bank has confirmed payment?', { confirmLabel: 'Mark Disbursed' }))) return;
    try {
      await api.post(`/benefit-payment-batches/${batch.id}/mark-disbursed`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  if (!batch) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/benefits/payment-batches')} />
          <h1>{batch.batch_number}</h1>
        </div>
        <div className="page-header-actions">
          <span className="badge badge-neutral">{batch.status}</span>
          {canDisburse && (
            <IconButton icon={IconDownload} label="Export CSV" onClick={exportBatch} />
          )}
          {canDisburse && batch.status !== 'disbursed' && (
            <button type="button" className="btn btn-primary" onClick={markDisbursed}>
              Mark Disbursed
            </button>
          )}
        </div>
      </header>

      <div className="card">
        <p>
          <strong>Total amount:</strong> {fmtMoney(batch.total_amount)}
        </p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Claim</th>
                <th>Member</th>
                <th>Bank</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {batch.lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">{line.benefit_claim.reference}</td>
                  <td>
                    {line.benefit_claim.member.user
                      ? `${line.benefit_claim.member.user.first_name} ${line.benefit_claim.member.user.last_name}`
                      : `Member #${line.benefit_claim.member.id}`}
                  </td>
                  <td className="muted">{line.bank_name} — {line.bank_account_name}</td>
                  <td>{fmtMoney(line.amount)}</td>
                  <td>
                    <span className="badge badge-neutral">{line.status}</span>
                  </td>
                </tr>
              ))}
              {batch.lines.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No lines in this batch.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
